// Compact horizontal Decision Timeline (Fase 3 completering).
// Design-only, no actions. Latest N nodes rendered left→right, newest right.
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { t } from "@/i18n";
import { Activity } from "lucide-react";

const OUTCOME_COLOR = {
  PROPOSED:          "bg-info-blue border-info-blue",
  VALIDATED_LATER:   "bg-pos-green border-pos-green",
  INVALIDATED_LATER: "bg-neg-red border-neg-red",
  INCONCLUSIVE:      "bg-tier-c-grey border-tier-c-grey",
};

const EVIDENCE_TIER = {
  MEASURED:                 "text-pos-green",
  EXPERIMENTALLY_SUPPORTED: "text-info-blue",
  MODELLED_ESTIMATE:        "text-warn-amber",
  DIRECTIONAL_ESTIMATE:     "text-warn-amber",
  UNKNOWN:                  "text-tier-c-grey",
};

export function DecisionTimeline({ testid = "decision-timeline" }) {
  const [items, setItems] = useState(null);
  const [hover, setHover] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/decision-twin/timeline?limit=10")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <div data-testid={testid}
          className="rounded-card border border-border-hairline bg-bg-panel p-4 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-3.5 h-3.5 text-accent-teal" />
        <h3 className="text-[11px] text-text-muted font-semibold"
             style={{ letterSpacing: "0.08em" }}>
          {t("timeline.title")}
        </h3>
      </div>

      {items === null ? (
        <div className="h-16 exec-skeleton rounded" />
      ) : items.length === 0 ? (
        <div className="py-2 text-sm text-text-muted"
              data-testid={`${testid}-empty`}>
          {t("timeline.empty")}
        </div>
      ) : (
        <div className="relative">
          <div ref={scrollRef}
                className="overflow-x-auto pb-4"
                data-testid={`${testid}-scroll`}>
            <div className="relative min-w-max">
              {/* baseline hairline */}
              <div className="absolute left-0 right-0 top-[36px] h-px bg-border-hairline" />
              <div className="flex items-start gap-8 px-2 min-w-max">
                {[...items].reverse().map((it) => {
                  const dateShort = it.created_at
                    ? new Date(it.created_at).toLocaleDateString("nl-NL",
                        { day: "2-digit", month: "short" })
                    : "—";
                  const color = OUTCOME_COLOR[it.outcome_status] || OUTCOME_COLOR.PROPOSED;
                  const label = (it.label || "—").slice(0, 24);
                  return (
                    <div key={it.id}
                          className="relative flex flex-col items-center w-28 select-none"
                          data-testid={`${testid}-node-${it.id}`}
                          onMouseEnter={() => setHover(it.id)}
                          onMouseLeave={() => setHover(null)}>
                      <span className="text-[10px] text-text-muted num mb-2">
                        {dateShort}
                      </span>
                      <span className={`w-3 h-3 rounded-full border ${color} relative z-10`} />
                      <span className="text-[11px] text-text-primary/85 text-center mt-2 leading-tight px-0.5"
                             title={it.label}>
                        {label}{(it.label || "").length > 24 ? "…" : ""}
                      </span>

                      {hover === it.id && (
                        <div className="absolute z-20 top-14 left-1/2 -translate-x-1/2 w-56 rounded-md border border-border-hairline bg-bg-panel-raised p-3 space-y-1.5 text-left"
                              style={{ boxShadow: "0 6px 20px rgba(0,0,0,0.45)" }}
                              data-testid={`${testid}-popover-${it.id}`}>
                          {it.evidence_class && (
                            <div>
                              <span className={`pill-outline ${EVIDENCE_TIER[it.evidence_class] || "text-text-muted"}`}>
                                {t(`enum.evidence.${it.evidence_class}`)}
                              </span>
                            </div>
                          )}
                          {it.confidence != null && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-text-muted">{t("timeline.tooltip_confidence")}</span>
                              <span className="text-xs text-text-primary num">
                                {typeof it.confidence === "number"
                                  ? `${Math.round(it.confidence * 100)}%`
                                  : it.confidence}
                              </span>
                            </div>
                          )}
                          {it.range_low != null && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-text-muted">{t("timeline.tooltip_range")}</span>
                              <span className="text-xs text-text-primary num">
                                € {Number(it.range_low).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}
                                {" – "}
                                € {Number(it.range_high).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                          )}
                          <Link
                            to={`/ghost-ledger#decision-${it.id}`}
                            data-testid={`${testid}-view-${it.id}`}
                            className="inline-block text-[11px] text-accent-teal hover:text-accent-teal-dim pt-1 transition-colors duration-exec"
                          >
                            {t("timeline.view_link")} →
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
